// useSourcePreview — fetch + cache a source's {label, summary, open} for the
// click-to-peek popover (docs/chronology-frontend.md). Reusable anywhere a
// source reference is shown (the chronology chips today; doc/discovery/pleading
// viewers next). Backs the cross-module source-preview API.
//
// The cache is a module-level Map keyed by case + source identity, so it lives
// for the whole session and a repeat peek of the same source is instant (no
// refetch). This is the seed of the future durable / "wired" local docs cache —
// a persistent store can slot in behind the same get/set without touching call
// sites.
import { useEffect, useState } from 'react';
import {
  fetchSourcePreview,
  type SourcePreview,
  type SourcePreviewRequest,
} from '../lib/database';

const _cache = new Map<string, SourcePreview>();

function cacheKey(caseId: string, req: SourcePreviewRequest): string {
  return `${caseId}:${req.source_kind}:${req.source_id}:${req.discovery_item_response_id ?? ''}`;
}

/** Clear the in-memory preview cache (e.g. on sign-out). */
export function clearSourcePreviewCache(): void {
  _cache.clear();
}

export interface UseSourcePreviewResult {
  preview: SourcePreview | null;
  loading: boolean;
  error: string | null;
}

/**
 * Resolve a source preview, served from the session cache when present.
 * @param caseId  the current case
 * @param req     the source to resolve, or null when the source isn't
 *                resolvable (an unlinked free-text cite) — then nothing fetches
 * @param enabled gate the fetch (e.g. only once the popover is open)
 */
export function useSourcePreview(
  caseId: string,
  req: SourcePreviewRequest | null,
  enabled: boolean,
): UseSourcePreviewResult {
  const reqKey = req ? cacheKey(caseId, req) : null;

  // State is the fetched result TAGGED with the key it was fetched for, plus a
  // keyed error. Everything the hook returns is then DERIVED against the CURRENT
  // reqKey at render time — so a request change (including to null) can never
  // surface a stale summary or keep a stale Open target live, even when the
  // preview component is reused. The hook is fully key-owned.
  const [loaded, setLoaded] = useState<{ key: string; value: SourcePreview } | null>(
    () => (reqKey && _cache.has(reqKey)) ? { key: reqKey, value: _cache.get(reqKey)! } : null,
  );
  const [error, setError] = useState<{ key: string; message: string } | null>(null);

  // Fetch on a cache miss for the current key; `active` drops a late response
  // after unmount / a request change. A cache hit needs no fetch (it's read in
  // the render-time derive below).
  useEffect(() => {
    if (!enabled || !req || !reqKey) return;
    if (_cache.has(reqKey)) {
      setLoaded({ key: reqKey, value: _cache.get(reqKey)! });
      setError(null);
      return;
    }
    let active = true;
    setError(null);
    fetchSourcePreview(caseId, req)
      .then((p) => {
        if (!active) return;
        _cache.set(reqKey, p);
        setLoaded({ key: reqKey, value: p });
      })
      .catch((e: unknown) => {
        if (!active) return;
        setError({ key: reqKey, message: e instanceof Error ? e.message : 'Could not load this source.' });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reqKey]);

  // Derive against the CURRENT reqKey: the tagged fetch result if it matches,
  // else a synchronous cache hit, else null. loading/error are likewise keyed.
  const cachedNow = reqKey ? _cache.get(reqKey) ?? null : null;
  const preview = reqKey ? (loaded && loaded.key === reqKey ? loaded.value : cachedNow) : null;
  const errorMsg = reqKey && error && error.key === reqKey ? error.message : null;
  const loading = !!reqKey && enabled && preview == null && errorMsg == null;

  return { preview, loading, error: errorMsg };
}
